import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import NavigatorManageTable, {
  nameSort,
  OverflowNameList,
  SimpleActionsMenu,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable for the Exercises page's
// Questions tab — mirrors ManageTagsTable's structure.

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const MutedText = styled.span`
  color: #616161;
`;

const DescriptionText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BULK_ACTIONS = [
  {
    key: 'deleteQuestions', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

// API inputType strings -> user-facing labels.
const INPUT_TYPE_LABELS = {
  RANGE: 'Range',
  INPUT: 'Input',
  'YES-NO': 'Yes/No',
  'MULTIPLE-CHOICE': 'Multiple Choice',
};

const typeLabel = (q) => INPUT_TYPE_LABELS[q.inputType] || q.inputType || '';

const NameCell = ({ row }) => {
  const { title } = row.original;
  return <NameText title={title}>{title || 'Untitled question'}</NameText>;
};

NameCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ title: PropTypes.string }) }).isRequired,
};

const DescriptionCell = ({ row }) => {
  const { description } = row.original;
  return description
    ? <DescriptionText title={description}>{description}</DescriptionText>
    : <MutedText>–</MutedText>;
};

DescriptionCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ description: PropTypes.string }) }).isRequired,
};

const ChoicesCell = ({ row }) => {
  const {
    id, inputType, choices, minRangeValue, maxRangeValue,
  } = row.original;
  if (inputType === 'RANGE' && minRangeValue != null && maxRangeValue != null) {
    return <MutedText>{`${minRangeValue} – ${maxRangeValue}`}</MutedText>;
  }
  if (!choices || !choices.length) return <MutedText>–</MutedText>;
  return (
    <OverflowNameList
      items={choices.map((c) => ({ id: c.id, name: c.displayValue }))}
      idPrefix='question-choices'
      rowId={id}
      variant='pill'
    />
  );
};

ChoicesCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      inputType: PropTypes.string,
      minRangeValue: PropTypes.number,
      maxRangeValue: PropTypes.number,
      choices: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        displayValue: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const QuestionActionsCell = ({ row }) => {
  const q = row.original;
  return <SimpleActionsMenu onEdit={() => q.onEdit(q)} onDelete={() => q.onDelete(q)} />;
};

QuestionActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

// Tokenized like exerciseSearch: every whitespace-separated term must hit
// the title, type label, or a choice value.
const questionSearch = (row, q) => {
  const haystack = [
    row.title || '',
    typeLabel(row),
    ...(row.choices || []).map((c) => c.displayValue || ''),
  ].join(' ').toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

const ManageQuestionsTable = ({
  questions, isLoading, hasError, onRetry, onEditQuestion, onDeleteQuestion, onBulkDelete,
  clearSelectionToken,
}) => {
  const tableData = React.useMemo(
    () => (questions || []).map((q) => ({
      ...q, name: q.title, onEdit: onEditQuestion, onDelete: onDeleteQuestion,
    })),
    [questions, onEditQuestion, onDeleteQuestion],
  );

  const columns = React.useMemo(() => [
    {
      Header: 'Question',
      accessor: 'name',
      sortType: nameSort,
      headerStyle: { minWidth: '200px', flex: '1' },
      cellStyle: { minWidth: '200px', flex: '1' },
      Cell: NameCell,
    },
    {
      Header: 'Description',
      accessor: 'description',
      disableSortBy: true,
      headerStyle: { width: '260px' },
      cellStyle: { width: '260px' },
      Cell: DescriptionCell,
    },
    {
      Header: 'Type',
      id: 'type',
      accessor: typeLabel,
      headerStyle: { width: '130px' },
      cellStyle: { width: '130px' },
    },
    {
      Header: 'Choices',
      id: 'choices',
      accessor: (row) => (row.choices || []).map((c) => c.displayValue).join(' '),
      disableSortBy: true,
      headerStyle: { width: '250px' },
      cellStyle: { width: '250px' },
      Cell: ChoicesCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '90px', textAlign: 'center' },
      cellStyle: { width: '90px', textAlign: 'center' },
      Cell: QuestionActionsCell,
    },
  ], []);

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'deleteQuestions') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading questions.'
      onRetry={onRetry}
      searchKeys={['name']}
      searchFn={questionSearch}
      searchPlaceholder='Search questions'
      emptyMessage='No questions yet.'
      searchEmptyMessage='No questions match your search.'
      entityLabel='question'
      defaultSortBy={[{ id: 'name', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={handleBulkAction}
      clearSelectionToken={clearSelectionToken}
      virtualizeRows
    />
  );
};

ManageQuestionsTable.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  onEditQuestion: PropTypes.func,
  onDeleteQuestion: PropTypes.func,
  onBulkDelete: PropTypes.func,
  clearSelectionToken: PropTypes.number,
};

ManageQuestionsTable.defaultProps = {
  questions: [],
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  onEditQuestion: () => {},
  onDeleteQuestion: () => {},
  onBulkDelete: () => {},
  clearSelectionToken: 0,
};

export default ManageQuestionsTable;
